import React, { createContext, useContext, ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { dashboardApi } from '../services/api';
import { useAuth } from './AuthContext';

interface FilterOptions {
  business_units: string[];
  sub_bus: string[];
  years: number[];
  months: number[];
  products: string[];
}


interface FilterOptionsContextType {
  filterOptions: FilterOptions | undefined;
  isLoading: boolean;
  error: any;
  refetch: () => void;
}

const FilterOptionsContext = createContext<FilterOptionsContextType | undefined>(undefined);

interface FilterOptionsProviderProps {
  children: ReactNode;
}

export const FilterOptionsProvider: React.FC<FilterOptionsProviderProps> = ({ children }) => {
  const { user } = useAuth();

  const {
    data: filterOptions,
    isLoading,
    error,
    refetch
  } = useQuery({
    queryKey: ['filter-options', user?.id], // Cache per user
    queryFn: async () => {
      const options = await dashboardApi.getFilterOptions();
      return {
        business_units: options.business_units || [],
        sub_bus: options.sub_bus || [],
        years: options.years || [2020, 2021, 2022, 2023, 2024, 2025],
        months: options.months || [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12],
        products: options.products || [],
      };

    },
    enabled: !!user, // Only fetch when user is available
    staleTime: 15 * 60 * 1000, // 15 minutes - filter options rarely change
    gcTime: 60 * 60 * 1000, // 1 hour cache time - keep in memory longer
    retry: 2, // Reduced retries for better performance
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 5000), // Faster retry
    refetchOnWindowFocus: false,
    refetchOnMount: false, // Don't refetch if we have cached data
    refetchOnReconnect: false, // Don't refetch on network reconnect
  });

  const value: FilterOptionsContextType = {
    filterOptions,
    isLoading,
    error,
    refetch,
  };

  return (
    <FilterOptionsContext.Provider value={value}>
      {children}
    </FilterOptionsContext.Provider>
  );
};

export const useFilterOptions = (): FilterOptionsContextType => {
  const context = useContext(FilterOptionsContext);
  if (context === undefined) {
    throw new Error('useFilterOptions must be used within a FilterOptionsProvider');
  }
  return context;
};
